import Heading from "Components/Heading";
import React, { useState } from "react";
import { Button, Card, Col, Form, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import Select from "react-select";
import { MdDateRange } from "react-icons/md";
import { BiSearch, BiHelpCircle } from "react-icons/bi";
import Footer from "Components/Footer";
import HourLogAPIs from "../../APIs/hour-log";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import * as XLSX from "xlsx-js-style";
import { capitalizeFirstLetter } from "Helper/Converters";
import { format } from "date-fns";
import CustomDatePicker from "Components/CustomDatePicker";
import { exportFileToExcel } from "Utils/utils";
import { buildHaltonRegionRoadsReportExcel } from "./billingUtils";

const DATE_TYPE_OPTIONS = [
  { value: "", label: "Select" },
  { value: "completeDate", label: "Completed Date" },
  { value: "enteredDate", label: "Entered Date" },
  { value: "dueDate", label: "Due Date" },
  { value: "assignDate", label: "Assigned Date" },
  { value: "cancelDate", label: "Cancelled Date" },
];
// lets add billing for halton hills
const CONTRACTS_OPTIONS = [
  { value: "", label: "Select" },
  { value: "AURORA", label: "Aurora" },
  { value: "BRAMPTON", label: "Brampton" },
  { value: "PEEL", label: "Peel" },
  { value: "PICKERING", label: "Pickering" },
  { value: "VAUGHAN", label: "Vaughan" },
  { value: "YORK", label: "York" },
  { value: "WATERLOO", label: "Waterloo" },
  { value: "HALTON HILLS", label: "Halton Hills" },
  { value: "HALTON REGION", label: "Halton Region" },
];

const CONTRACTS_UTILS_OPTIONS = [
  { value: "", label: "Select" },
  { value: "waterUnit", label: "Water" },
  { value: "stormUnit", label: "Storm" },
  { value: "sanitaryUnit", label: "Sanitary" },
];

// Contracts that have no utility breakdown (dropdown should show only "Select")
const CONTRACTS_WITHOUT_UTILITIES = ["HALTON HILLS"];

const HALTON_REGION_VALUE = "HALTON REGION";

const HALTON_CITIES = [
  { value: "", label: "Select" },
  { value: "BURLINGTON", label: "Burlington" },
  { value: "OAKVILLE", label: "Oakville" },
  { value: "HALTON_HILLS", label: "Halton Hills" },
  { value: "MILTON", label: "Milton" },
  { value: "ALL", label: "Water Wastewater" },
];

const HALTON_UTILITY_LABELS = {
  TRAFFIC_COM: "Traffic Com",
  REGIONAL_STORM: "Regional Storm",
  STREET_LIGHT: "Street Light",
  TRAFFIC_LIGHT: "Traffic Light",
  WASTE_WATER_WATER_ONLY: "Water Only",
  WASTE_WATER_SEWER_ONLY: "Sewer Only",
  WATER_TELECOM: "Water Telecom",
  SANITARY_TELECOM: "Sanitary Telecom",
};

const HALTON_CITY_UTILITIES = {
  BURLINGTON: [
    "TRAFFIC_COM",
    "REGIONAL_STORM",
    "STREET_LIGHT",
    "TRAFFIC_LIGHT",
  ],
  OAKVILLE: [
    "TRAFFIC_COM",
    "REGIONAL_STORM",
    "STREET_LIGHT",
    "TRAFFIC_LIGHT",
  ],
  HALTON_HILLS: [
    "TRAFFIC_COM",
    "REGIONAL_STORM",
    "STREET_LIGHT",
    "TRAFFIC_LIGHT",
  ],
  MILTON: [
    "TRAFFIC_COM",
    "REGIONAL_STORM",
    "STREET_LIGHT",
    "TRAFFIC_LIGHT",
  ],
  ALL: [
    "WASTE_WATER_WATER_ONLY",
    "WASTE_WATER_SEWER_ONLY",
    "WATER_TELECOM",
    "SANITARY_TELECOM",
  ],
};

const CITY_UTILITY_STATION_CODES = {
  BURLINGTON: {
    TRAFFIC_COM: ["HLTNBTC01"],
    REGIONAL_STORM: ["HLTNB01"],
    STREET_LIGHT: ["HLTNBSL01", "HMTBSL01"],
    TRAFFIC_LIGHT: ["HLTNBTS01", "HMTBTS01"],
  },
  OAKVILLE: {
    TRAFFIC_COM: ["HLTNOTC01"],
    REGIONAL_STORM: ["HLTNOK01"],
    STREET_LIGHT: ["HLTNOSL01", "HMTOSL01"],
    TRAFFIC_LIGHT: ["HLTNOTS01", "HMTOTS01"],
  },
  HALTON_HILLS: {
    TRAFFIC_COM: ["HLTNHTC01"],
    REGIONAL_STORM: ["HLTNSS04"],
    STREET_LIGHT: ["HLTNHSL01", "HMTHSL01"],
    TRAFFIC_LIGHT: ["HLTNHTS01", "HMTHTS01"],
  },
  MILTON: {
    TRAFFIC_COM: ["HLTNMTC01"],
    REGIONAL_STORM: ["HLTNM01"],
    STREET_LIGHT: ["HLTNMSL01", "HMTMSL01"],
    TRAFFIC_LIGHT: ["HLTNMTS01", "HMTMTS01"],
  },
  ALL: {
    WASTE_WATER_WATER_ONLY: ["HLTNW02"],
    WASTE_WATER_SEWER_ONLY: ["HLTNW02"],
    WATER_TELECOM: ["HLTNT01"],
    SANITARY_TELECOM: ["HLTNT01"],
  },
};


const getHaltonUtilityOptions = (haltonCityValue) => {
  if (!haltonCityValue || !HALTON_CITY_UTILITIES[haltonCityValue])
    return [{ value: "", label: "Select" }];
  return [
    { value: "", label: "Select" },
    ...HALTON_CITY_UTILITIES[haltonCityValue].map((key) => ({
      value: key,
      label: HALTON_UTILITY_LABELS[key] || key,
    })),
  ];
};

const INITIAL_VALUES = {
  dateType: DATE_TYPE_OPTIONS[1],
  fromDate: new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000),
  toDate: new Date(),
  city: CONTRACTS_OPTIONS[0],
  haltonCity: HALTON_CITIES[0],
  filterUnit: CONTRACTS_UTILS_OPTIONS[0],
};

const ValidationSchema = Yup.object().shape({
  dateType: Yup.object()
    .shape({
      value: Yup.string().required("Date Type is required"),
    })
    .required("Date Type is required"),
  fromDate: Yup.date().required("Start Date is required"),
  toDate: Yup.date().required("End Date is required"),
  city: Yup.object()
    .shape({
      value: Yup.string().required("Contracts is required"),
    })
    .required("City is required"),
  haltonCity: Yup.object().when("city", {
    is: (city) => city?.value === HALTON_REGION_VALUE,
    then: (schema) => schema.shape({ value: Yup.string() }),
    otherwise: (schema) => schema.strip(),
  }),
  filterUnit: Yup.object().shape({ value: Yup.string() }),
});

const Billing = () => {
  const [reports, setReports] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [searchedValues, setSearchedValues] = useState(null);
  const [haltonReportExporting, setHaltonReportExporting] = useState(false);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "44px",
    }),
  };

  const fetchBillingData = async (values, setSubmitting) => {
    try {
      setReports([]);
      setTickets([]);
      setSearchedValues(JSON.parse(JSON.stringify(values)));
      // Assuming getQueryHourLog is a function that fetches data and returns a response similar to the one you provided
      const isHaltonRegion = values.city?.value === HALTON_REGION_VALUE;
      const apiPayload = {
        ...values,
        fromDate: format(new Date(values?.fromDate), "yyyy-MM-dd"),
        toDate: format(new Date(values?.toDate), "yyyy-MM-dd"),
        city: isHaltonRegion ? values.haltonCity : values.city,
        filterUnit: values.filterUnit,
      };
      const response = await HourLogAPIs.getBillingReport(apiPayload);
      if (response && response.data.success) {
        setReports(response.data.data.report);
        setTickets(response.data.data.tickets || []);
      } else {
        toast.error("No Records Found Yet!");
      }
    } catch (error) {
      console.error("Error fetching hour logs:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateForExcel = () => {
    let formattedData = [];
    reports.forEach((item) => {
      formattedData.push([
        {
          v: item.title,
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "Quantity",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
      ]);
      item.details.map((innerItem) => {
        formattedData.push([{ v: innerItem.title }, { v: innerItem.value }]);
      });
    });
    formattedData.push([]);
    if (!(tickets.length > 0)) return formattedData;
    formattedData.push([
      {
        v: "TicketNumber",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },
      {
        v: "Address",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },
      {
        v: "City",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },
      {
        v: "Contractor",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },
      {
        v: "Type of Ticket",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },
      {
        v: "Entered Date",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },
      {
        v: "Pre Completed Date",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },
      {
        v: "Station Code",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },
      ...Object.entries(tickets[0])
        .filter(([key, _]) => key.endsWith("_Unit"))
        .map(([key, _]) => {
          return {
            v: key,
            t: "s",
            s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
          };
        }),
      {
        // v: searchedValues.city.value == "BRAMPTON" ? "Office Clear" : "",
        v: " Office Clear",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },

      {
        // v: searchedValues.city.value == "BRAMPTON" ? "Office Clear" : "",
        v: " Office Clear Category",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },

    ]);

    tickets.forEach((item) => {
      if (item) {
        formattedData.push([
          { v: item.ticketno },
          { v: item.address },
          { v: item.city },
          { v: item.contractorname },
          { v: item.type },
          { v: format(item.entereddate, "dd/MM/yyyy") },
          {
            v: item.preCompletedDate
              ? format(new Date(item.preCompletedDate), "dd/MM/yyyy")
              : "",
          },
          { v: item.stationcodes },
          ...Object.entries(item)
            .filter(([key, _]) => key.endsWith("_Unit"))
            .map(([key, value]) => {
              return {
                v: value,
              };
            }),
          // { v: searchedValues.city.value == 'BRAMPTON' ?? item.oc },
          { v: parseInt(item.oc) },
          { v: item.officeClearCategory },
        ]);
      }
    });

    return formattedData;
  };

  const exportToExcel = () => {
    const data = formatDateForExcel(); // Assuming this function returns an array of arrays (rows)
    const fileName = "Billing_Report";
    exportFileToExcel(data, fileName);
  };

  const handleHaltonRegionRoadsReportExport = async (values) => {
    if (!values?.fromDate || !values?.toDate || !values?.dateType?.value) {
      toast.error("Please select Date Type, Start Date, and End Date.");
      return;
    }
    setHaltonReportExporting(true);
    try {
      const fromDate = format(new Date(values.fromDate), "yyyy-MM-dd");
      const toDate = format(new Date(values.toDate), "yyyy-MM-dd");
      const dateType = values.dateType;
      const response = await HourLogAPIs.getHaltonRegionRoadsReport({
        fromDate,
        toDate,
        dateType,
      });
      if (response?.data?.success && response?.data?.data) {
        const excelData = buildHaltonRegionRoadsReportExcel(response.data.data);
        exportFileToExcel(excelData, "Halton_Region_Billing_Report");
      } else {
        toast.error("No data returned for the report.");
      }
    } catch (error) {
      console.error("Halton Region Roads Report export error:", error);
      toast.error(
        error?.response?.data?.message || "Failed to generate Halton Region Roads Report."
      );
    } finally {
      setHaltonReportExporting(false);
    }
  };

  return (
    <>
      <section>
        <Heading text={"Billing"} />

        <Card>
          <Formik
            initialValues={INITIAL_VALUES}
            validationSchema={ValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
              const isHaltonRegion = values.city?.value === HALTON_REGION_VALUE;
              const hasCity =
                values.haltonCity?.value &&
                values.haltonCity.value !== "";
              const hasUtility =
                values.filterUnit?.value &&
                values.filterUnit.value !== "";
              if (isHaltonRegion && (!hasCity || !hasUtility)) {
                toast.error("Select city and utility both for Halton Region");
                setSubmitting(false);
                return;
              }
              fetchBillingData(values, setSubmitting);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              /* and other goodies */
            }) => {
              return (
                <Form className="dashboard-form mb-4" onSubmit={handleSubmit}>
                  <Row>
                    <Col className="column">
                      <Form.Group className="form-group">
                        <Form.Label>Date Type</Form.Label>
                        <Select
                          name="dateType"
                          options={DATE_TYPE_OPTIONS}
                          value={values.dateType}
                          styles={customStyles}
                          onChange={(val) => setFieldValue("dateType", val)}
                          onBlur={() => setFieldTouched("dateType", true, true)}
                        />
                        {touched.dateType && errors.dateType?.value && (
                          <span className="error-msg">
                            {errors.dateType.value}
                          </span>
                        )}
                      </Form.Group>
                    </Col>
                    <Col className="column">
                      <Form.Group className="form-group">
                        <Form.Label>Start Date</Form.Label>
                        <div className="datepicker_box">
                          <CustomDatePicker
                            name="fromDate"
                            selected={values.fromDate}
                            onSelect={(date) => {
                              setFieldValue("fromDate", date);
                              if (
                                values.toDate &&
                                values.toDate.getTime() < date.getTime()
                              )
                                setFieldValue("toDate", null);
                            }}
                            dateFormat="MM/dd/yyyy" // Adjust the date format as needed
                            onBlur={() =>
                              setFieldTouched("fromDate", true, true)
                            }
                          />
                          <span>
                            <MdDateRange />
                          </span>
                        </div>
                        {touched.fromDate && errors.fromDate && (
                          <span className="error-msg">{errors.fromDate}</span>
                        )}
                      </Form.Group>
                    </Col>
                    <Col className="column">
                      <Form.Group className="form-group">
                        <Form.Label>Date To</Form.Label>
                        <div className="datepicker_box">
                          <CustomDatePicker
                            name="toDate"
                            minDate={values.fromDate}
                            selected={values.toDate}
                            onSelect={(date) => setFieldValue("toDate", date)}
                            dateFormat="MM/dd/yyyy" // Adjust the date format as needed
                            onBlur={() => setFieldTouched("toDate", true, true)}
                          />
                          <span>
                            <MdDateRange />
                          </span>
                        </div>
                        {touched.toDate && errors.toDate && (
                          <span className="error-msg">{errors.toDate}</span>
                        )}
                      </Form.Group>
                    </Col>
                    <Row className="pe-0">
                      <Col
                        md={
                          values.city?.value === HALTON_REGION_VALUE ? 4 : 6
                        }
                      >
                        <Form.Group className="form-group">
                          <Form.Label>Contracts</Form.Label>
                          <Select
                            name="city"
                            options={CONTRACTS_OPTIONS}
                            value={values.city}
                            styles={customStyles}
                            onChange={(val) => {
                              setFieldValue("city", val);
                              const isHalton =
                                val?.value === HALTON_REGION_VALUE;
                              if (!isHalton) {
                                setFieldValue("haltonCity", HALTON_CITIES[0]);
                              }
                              if (
                                val?.value &&
                                CONTRACTS_WITHOUT_UTILITIES.includes(
                                  val.value
                                )
                              ) {
                                setFieldValue(
                                  "filterUnit",
                                  CONTRACTS_UTILS_OPTIONS[0]
                                );
                              }
                              if (isHalton) {
                                setFieldValue(
                                  "filterUnit",
                                  getHaltonUtilityOptions(
                                    values.haltonCity?.value
                                  )[0]
                                );
                              }
                            }}
                            onBlur={() => setFieldTouched("city", true, true)}
                          />
                          {touched.city && errors.city?.value && (
                            <span className="error-msg">
                              {errors.city.value}
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      {values.city?.value === HALTON_REGION_VALUE && (
                        <Col md={4}>
                          <Form.Group className="form-group">
                            <Form.Label>City</Form.Label>
                            <Select
                              name="haltonCity"
                              options={HALTON_CITIES}
                              value={values.haltonCity}
                              styles={customStyles}
                              onChange={(val) => {
                                setFieldValue("haltonCity", val);
                                setFieldValue(
                                  "filterUnit",
                                  getHaltonUtilityOptions(val?.value)[0]
                                );
                              }}
                              onBlur={() =>
                                setFieldTouched("haltonCity", true, true)
                              }
                            />
                            {touched.haltonCity &&
                              errors.haltonCity?.value && (
                                <span className="error-msg">
                                  {errors.haltonCity.value}
                                </span>
                              )}
                          </Form.Group>
                        </Col>
                      )}
                      <Col
                        md={
                          values.city?.value === HALTON_REGION_VALUE ? 4 : 6
                        }
                        className="mb-3 pe-0"
                      >
                        <Form.Group className="form-group">
                          <Form.Label>Contract Utilities</Form.Label>
                          <Select
                            name="filterUnit"
                            options={
                              values.city?.value === HALTON_REGION_VALUE
                                ? getHaltonUtilityOptions(
                                  values.haltonCity?.value
                                )
                                : values.city?.value &&
                                  CONTRACTS_WITHOUT_UTILITIES.includes(
                                    values.city.value
                                  )
                                  ? [CONTRACTS_UTILS_OPTIONS[0]]
                                  : CONTRACTS_UTILS_OPTIONS
                            }
                            value={values.filterUnit}
                            styles={customStyles}
                            onChange={(val) =>
                              setFieldValue("filterUnit", val)
                            }
                            onBlur={() =>
                              setFieldTouched("filterUnit", true, true)
                            }
                          />
                          {touched.filterUnit && errors.filterUnit?.value && (
                            <span className="error-msg">
                              {errors.filterUnit.value}
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={12} className="d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2">
                          {reports.length > 0 && (
                            <Button onClick={exportToExcel}>Export</Button>
                          )}
                          {values.city?.value === HALTON_REGION_VALUE && (
                            <span className="position-relative d-inline-block">
                              <Button
                                onClick={() =>
                                  handleHaltonRegionRoadsReportExport(values)
                                }
                                disabled={haltonReportExporting}
                              >
                                {haltonReportExporting
                                  ? "Loading..."
                                  : "BILLING REPORT"}
                              </Button>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="billing-report-tooltip">
                                    No need to select City or Utility for Billing Report
                                  </Tooltip>
                                }
                              >
                                <span
                                  className="position-absolute d-flex align-items-center justify-content-center text-muted"
                                  style={{
                                    top: "-18px",
                                    right: "-14px",
                                    width: "16px",
                                    height: "16px",
                                    fontSize: "11px",
                                    cursor: "pointer",
                                    backgroundColor: "var(--bs-body-bg, #fff)",
                                    borderRadius: "50%",
                                    border: "1px solid var(--bs-border-color)",
                                  }}
                                >
                                  <BiHelpCircle size={12} />
                                </span>
                              </OverlayTrigger>
                            </span>
                          )}
                        </div>
                        <Button type="submit" disabled={isSubmitting}>
                          <BiSearch />
                          {isSubmitting ? "Loading..." : "Search"}
                        </Button>
                      </Col>
                      {/* <Col md={12} className="d-flex justify-content-between mt-3">
                     
                      </Col> */}
                    </Row>
                  </Row>
                </Form>
              );
            }}
          </Formik>
          {reports.map((item, index) => {
            const isHaltonRegion =
              searchedValues?.city?.value === HALTON_REGION_VALUE;
            return (
              <Table
                key={index}
                striped
                bordered
                hover
                className="mt-3 billing-report-custom-table"
              >
                <thead>
                  <tr>
                    <th>
                      <span>{item.title}</span>
                    </th>
                    <th>
                      <span>Quantity</span>
                    </th>
                    {isHaltonRegion && (
                      <th>
                        <span>Additionals</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {item.details.map((innerItem, innerIndex) => (
                    <tr key={innerIndex}>
                      <td>{innerItem.title}</td>
                      <td>{innerItem.value}</td>
                      {isHaltonRegion && (
                        <td>{innerItem.additionals ?? ""}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            );
          })}
          <Footer />
        </Card>
      </section>
    </>
  );
};

export default Billing;
